import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { ApiError } from "../../lib/api";
import { Button, ErrorNotice, Field } from "../../components/ui";
import { AuthFrame } from "./AuthFrame";

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    trading_name: "",
    registration_number: "",
    contact_name: "",
    email: "",
    phone: "",
    password: "",
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const fieldError = (name: string) =>
    error instanceof ApiError ? error.fieldError(name) : undefined;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register(form);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame
      title="Register your business"
      subtitle="Start building against the sandbox straight away. Verification unlocks live payments."
      wide
      footer={
        <>
          Already registered?{" "}
          <Link to="/signin" className="font-medium text-verdigris underline underline-offset-2">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <ErrorNotice error={error} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Registered company name"
            required
            value={form.company_name}
            onChange={set("company_name")}
            error={fieldError("company_name")}
            placeholder="Kariba Retail (Private) Limited"
            className="sm:col-span-2"
          />
          <Field
            label="Trading name"
            value={form.trading_name}
            onChange={set("trading_name")}
            hint="If different from the registered name"
          />
          <Field
            label="Company registration number"
            value={form.registration_number}
            onChange={set("registration_number")}
            placeholder="12345/2024"
          />
          <Field
            label="Your full name"
            required
            value={form.contact_name}
            onChange={set("contact_name")}
            error={fieldError("contact_name")}
          />
          <Field
            label="Mobile number"
            value={form.phone}
            onChange={set("phone")}
            placeholder="0771234567"
          />
          <Field
            label="Work email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={set("email")}
            error={fieldError("email")}
            className="sm:col-span-2"
          />
          <Field
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={form.password}
            onChange={set("password")}
            error={fieldError("password")}
            hint="At least 12 characters. Length matters more than symbols."
            className="sm:col-span-2"
          />
        </div>

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Create account
        </Button>

        <p className="text-[11.5px] leading-relaxed text-ink-muted">
          By registering you agree that payments are processed through our licensed
            banking partner and settled by Bushveld under the fee schedule shown in
            your dashboard.
        </p>
      </form>
    </AuthFrame>
  );
}
